import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Play, Trophy, Users, Heart, Zap, Star, LogOut, LogIn, Settings as SettingsIcon, User as UserIcon, Target, Apple as AppleIcon, Ticket, X, ShoppingBag, Menu as MenuIcon, MessageSquareText, FileText, Timer, Clock, ListChecks, Coins, Loader2, CheckCircle2, AlertCircle, Sparkles, Check, Package, Layout, Flag, Crown, Calendar, Cloud, Shell, Layers, Gift, ArrowLeft } from 'lucide-react';
import { UserData } from '../hooks/useUser';
import { cn } from '../lib/utils';
import CurrencyIcon from './CurrencyIcon';
import DailyGiftIcon from './DailyGiftIcon';
import Inventory from './Inventory';
import GameModal, { ModalType } from './GameModal';
import { audioEngine } from '../utils/audioEngine';
import GalleryPhoto from './GalleryPhoto';
import AvatarFrame from './AvatarFrame';
import PremiumModal from './PremiumModal';
import PrismManager from './PrismManager';
import PrismUnboxingModal from './PrismUnboxingModal';
import { adManager } from '../utils/adManager';
import ChestIcon from './ChestIcon';
import FeatureIcon, { FeatureIconType } from './FeatureIcon';
import LevelMedal from './LevelMedal';
import { Prism, getBaseTraitValue } from '../utils/prismSystem';
import { StarIcon, ShellIcon, CloudIcon, GoldRushIcon } from './ModeIcons';
import Prism3D from './Prism3D';
import SceneBackground from './SceneBackground';
import { MILESTONE_OFFERS } from '../utils/milestoneOffers';
import { purchaseManager } from '../utils/purchaseManager';
import { useTranslation } from 'react-i18next';

interface HomeProps {
  user: UserData | null;
  lastScore: number;
  secondsToNextHeart: number | null;
  onStartGame: (mode: 'pemdas' | 'shellcrush' | 'starburst' | 'goldrush') => void;
  onShowLeaderboard: () => void;
  onShowFriends: () => void;
  onShowProfile: () => void;
  onShowSettings: () => void;
  onUpdateNickname: (nickname: string) => Promise<{ success: boolean; error?: string }>;
  onBuyTicket: () => Promise<boolean>;
  onRedeemTicket: () => Promise<boolean>;
  onAddTickets: (amount: number) => Promise<void>;
  onShowMissions: () => void;
  onShowDailyDrop: () => void;
  onUpdateMission?: (id: string, amount?: number) => void;
  onClaimDailyGift: (track: 'basic' | 'premium', double?: boolean) => Promise<{ type: string; amount: number; isDouble: boolean } | null>;
  startingMode?: 'pemdas' | 'shellcrush' | 'starburst' | 'goldrush' | null;
  onCancelStart?: () => void;
  onOpenBox: (tier: keyof UserData['rewardBoxes']) => void;
  onBuyBox: (tier: keyof UserData['rewardBoxes'], costType: 'coins' | 'gems' | 'ad', costAmount: number) => Promise<boolean>;
  onShowShop: (tab?: 'market' | 'bank') => void;
  onShowPrismManager: () => void;
  onShowChooseMode: () => void;
  onSignInGoogle: () => Promise<{ success: boolean; error?: string }>;
  onSetPremiumStatus: (status: boolean) => Promise<void>;
  onShowAlert: (title: string, message: string, type?: ModalType) => void;
  unboxPrism: () => Promise<Prism | null>;
  totalClaimableMissions?: number;
  onShowInventory: () => void;
  hideBottomNav?: boolean;
  onExpireMilestoneOffer?: (id: string) => Promise<void>;
  onGrantMilestoneOfferRewards?: (id: string) => Promise<boolean>;
  news?: any[];
  showMainMenu: boolean;
  onShowMainMenuChange: (open: boolean) => void;
}
export default function Home({
  user,
  lastScore,
  secondsToNextHeart,
  onStartGame,
  onShowLeaderboard,
  onShowFriends,
  onShowProfile,
  onShowSettings,
  onUpdateNickname,
  onBuyTicket,
  onRedeemTicket,
  onAddTickets,
  onShowMissions,
  onShowDailyDrop,
  onUpdateMission,
  onClaimDailyGift,
  startingMode,
  onCancelStart,
  onOpenBox,
  onBuyBox,
  onShowShop,
  onShowPrismManager,
  onShowChooseMode,
  onSignInGoogle,
  onSetPremiumStatus,
  onShowAlert,
  unboxPrism,
  news = [],
  totalClaimableMissions = 0,
  onShowInventory,
  hideBottomNav = false,
  onExpireMilestoneOffer,
  onGrantMilestoneOfferRewards,
  showMainMenu,
  onShowMainMenuChange
}: HomeProps) {
  const { t } = useTranslation();

  const closeGameSelector = () => {
    onCancelStart?.();
  };
  const [showHeartAlert, setShowHeartAlert] = useState(false);
  const [nicknameInput, setNicknameInput] = useState('');
  const [showNicknameModal, setShowNicknameModal] = useState(false);
  const [nicknameError, setNicknameError] = useState<string | null>(null);
  const [isUpdatingNickname, setIsUpdatingNickname] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState<'google' | 'guest' | null>(null);
  const setShowMainMenu = onShowMainMenuChange;
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showPrismUnboxing, setShowPrismUnboxing] = useState(false);
  const [latestUnboxedPrism, setLatestUnboxedPrism] = useState<Prism | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isWatchingAd, setIsWatchingAd] = useState(false);
  const [isHoveringPlay, setIsHoveringPlay] = useState(false);
  const [offerTimeLeft, setOfferTimeLeft] = useState<string>('');
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [hasNewNews, setHasNewNews] = useState(false);
  const [expandedNewsId, setExpandedNewsId] = useState<string | null>(null);
  const [selectedNewsItem, setSelectedNewsItem] = useState<{ id: string; category: string; icon?: string; iconColor?: string; title: string; detail: string } | null>(null);

  const [showMilestonePopup, setShowMilestonePopup] = useState(false);
  const [isMilestoneProcessing, setIsMilestoneProcessing] = useState<string | null>(null);
  const [simulatingMilestoneOffer, setSimulatingMilestoneOffer] = useState<any>(null);

  // Sync news read version to control notification badge
  useEffect(() => {
    if (news && news.length > 0) {
      const latestId = news[0].id;
      const readVersion = localStorage.getItem('nr_news_read_version');
      setHasNewNews(readVersion !== latestId);
    } else {
      setHasNewNews(false);
    }
  }, [news]);

  // Auto-trigger milestone popup when available
  useEffect(() => {
    if (user?.activeMilestoneOffer) {
      const offerId = user.activeMilestoneOffer.id;
      const hasShown = localStorage.getItem(`shown_milestone_${offerId}`);
      if (!hasShown) {
        setShowMilestonePopup(true);
        localStorage.setItem(`shown_milestone_${offerId}`, 'true');
      }
    }
  }, [user?.activeMilestoneOffer]);

   // Handled by prop onShowAlert

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!user || !user.activeMilestoneOffer) {
      setOfferTimeLeft('');
      return;
    }

    const expiresAt = new Date(user.activeMilestoneOffer.expiresAt).getTime();
    
    const updateTime = () => {
      const now = Date.now();
      const remainingMs = expiresAt - now;
      if (remainingMs <= 0) {
        if (onExpireMilestoneOffer && user.activeMilestoneOffer) {
          onExpireMilestoneOffer(user.activeMilestoneOffer.id);
        }
        setOfferTimeLeft('');
        clearInterval(timer);
        return;
      }

      const totalSeconds = Math.floor(remainingMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const pad = (n: number) => String(n).padStart(2, '0');
      setOfferTimeLeft(`${pad(hours)}:${pad(minutes)}:${pad(seconds)}`);
    };

    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, [user?.activeMilestoneOffer, onExpireMilestoneOffer]);


  useEffect(() => {
    if (user && user.nickname === null && (user.isTutorialCompleted || user.tutorialStep === 36)) {
      setShowNicknameModal(true);
    }
  }, [user]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatClock = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const [updateStatus, setUpdateStatus] = useState<'idle' | 'checking' | 'saving' | 'success' | 'error'>('idle');

  const handleUpdateNickname = async () => {
    if (nicknameInput.trim() && updateStatus === 'idle') {
      setUpdateStatus('checking');
      setNicknameError(null);
      
      try {
        const result = await onUpdateNickname(nicknameInput.trim());
        
        if (result.success) {
          setUpdateStatus('success');
          setTimeout(() => {
            setShowNicknameModal(false);
            setUpdateStatus('idle');
          }, 1500);
        } else {
          setUpdateStatus('error');
          const errorMsg = result.error === 'NAME_TAKEN' ? t('home.nickname.error_taken') :
                           result.error === 'TIMEOUT' ? t('home.nickname.error_timeout') :
                           result.error === 'INDEX_MISSING' ? t('home.nickname.error_index') :
                           result.error === 'GUESTS_CANNOT_CHANGE_NAME' ? t('home.nickname.error_guest') :
                           t('home.nickname.error_generic');
          setNicknameError(errorMsg);
          setTimeout(() => setUpdateStatus('idle'), 2000);
        }
      } catch (err) {
        setUpdateStatus('error');
        setNicknameError(t('home.nickname.error_connection'));
        setTimeout(() => setUpdateStatus('idle'), 2000);
      }
    }
  };


  if (!user) {
    return null;
  }

  const expToNext = user.level * 1000;
  const expProgress = (user.exp / expToNext) * 100;
  const maxHearts = 5;

  return (
    <div className="flex flex-col h-full bg-slate-900 text-slate-900 relative overflow-hidden">

      {/* ── Unified Animated Background ── */}
      <SceneBackground />
      <div className="flex-1 flex flex-col relative">


      {/* Persistent Fixed Top Bar - Individual Glass Pills */}
      <div className="absolute top-5 left-0 right-0 z-[100] flex justify-center pointer-events-none">
        <div className="w-full max-w-md flex items-center justify-between px-6 gap-2 pointer-events-auto">
          {/* Hearts Pill */}
          <button
            id="header-hearts"
            onClick={() => setShowHeartAlert(true)}
            aria-label="Hearts refill"
            className="flex-1 bg-slate-50 pl-3 pr-2 py-1 rounded-full flex items-center justify-between gap-1.5 shadow-sm border border-slate-100 hover:scale-102 transition-transform active:scale-95 pointer-events-auto group cursor-pointer"
          >
            <div className="flex items-center gap-1 min-w-0">
              <Heart className="w-4 h-4 text-[#f43f5e] fill-[#f43f5e] shrink-0" />
              <span className="font-num text-xs text-slate-900 font-black">{user.hearts} / 5</span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {user.hearts >= maxHearts ? (
                <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse border border-white shadow-[0_0_8px_rgba(16,185,129,0.6)]" title="MAX" />
              ) : (
                <>
                  <span className="text-[8px] text-slate-400 font-black tabular-nums truncate block">
                    {secondsToNextHeart ? formatTime(secondsToNextHeart) : '--:--'}
                  </span>
                  <div className="w-5 h-5 rounded-full bg-[#f43f5e] text-white flex items-center justify-center font-black text-[10px] shadow-sm border border-white/50 shrink-0 group-hover:scale-110 transition-transform">
                    +
                  </div>
                </>
              )}
            </div>
          </button>

          {/* Coins Pill - Read Only */}
          <div id="header-coins" className="flex-1 bg-slate-50 px-3 py-1.5 rounded-full flex items-center justify-between gap-1.5 shadow-sm border border-slate-100 pointer-events-auto">
            <CurrencyIcon type="coin" size="sm" className="shrink-0" />
            <div className="min-w-0 text-right flex-1">
              <span className="font-num text-xs text-slate-900 font-black truncate block pr-1">
                {user.coins > 999999999 ? t('home.coins_overflow') : user.coins?.toLocaleString()}
              </span>
            </div>
          </div>

          {/* Gems Pill */}
          <button 
            id="header-gems"
            onClick={() => onShowShop('bank')}
            aria-label="Gems shop and currency bank"
            className="flex-1 bg-slate-50 pl-2.5 pr-2 py-1 rounded-full flex items-center justify-between gap-1.5 shadow-sm border border-slate-100 hover:scale-102 transition-transform active:scale-95 pointer-events-auto group cursor-pointer"
          >
            <CurrencyIcon type="gem" size="sm" className="shrink-0" />
            <div className="min-w-0 text-right flex-1">
              <span className="font-num text-xs text-slate-900 font-black truncate block pr-1">
                {user.gems > 99999999 ? t('home.gems_overflow') : user.gems?.toLocaleString()}
              </span>
            </div>
            {/* Small Actionable Plus Circle */}
            <div className="w-5 h-5 rounded-full bg-[#0ea5e9] text-white flex items-center justify-center font-black text-[10px] shadow-sm border border-white/50 shrink-0 group-hover:scale-110 transition-transform">
              +
            </div>
          </button>
        </div>
      </div>
 
      {/* Home-Specific Sub-Header (Image-Based Layout + Glass Theme) */}
      <div className="absolute top-[3.75rem] left-0 right-0 z-[90] flex justify-center pointer-events-none">
        <div className="w-full max-w-md flex justify-between items-start px-8 pointer-events-auto">
          {/* Profile Level Card (Left Side) - Dark Glass Version */}
          <button 
            onClick={onShowProfile}
            aria-label="Open profile page"
            className="flex items-center gap-2 bg-slate-900/60 backdrop-blur-md pl-1 pr-3 py-1 rounded-[18px] shadow-[0_4px_12px_rgba(0,0,0,0.3)] group active:scale-95 transition-all border border-cyan-500/30 text-white mt-0.5"
          >
            <AvatarFrame size="sm">
              <GalleryPhoto id={user.avatarId || 'g1'} size="sm" className="w-full h-full" />
            </AvatarFrame>
            <div className="flex items-center gap-1 leading-none shrink-0">
              {user.isPremium && <Crown className="w-3.5 h-3.5 text-amber-400 fill-amber-400 mr-0.5 shrink-0" />}
              <span className="text-[10px] font-black text-cyan-400 uppercase tracking-tight">{t('home.level_label')}</span>
              <span className="text-lg font-num text-white drop-shadow-[0_0_6px_rgba(34,211,238,0.5)]">{user.level}</span>
            </div>
          </button>
 
          {/* Action Row (Right Side) */}
          <div className="flex items-start gap-3">
            {/* Missions */}
            <div className="flex flex-col items-center gap-1">
              <button 
                onClick={onShowMissions}
                aria-label="Open missions checklist"
                className="relative w-11 h-11 rounded-full flex items-center justify-center bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-[#5eead4] via-[#0d9488] to-[#115e59] text-white shadow-xl active:scale-90 transition-all toy-3d-button border-b-4 border-[#042f2e]/60"
              >
                <FeatureIcon type="checklist" size="sm" className="brightness-110 saturate-125" />
                {totalClaimableMissions > 0 && (
                  <div className="absolute -top-2 -right-2 min-w-[20px] h-[20px] px-1 bg-[#f43f5e] border-2 border-white rounded-full shadow-lg flex items-center justify-center animate-pulse z-20">
                     <span className="text-[10px] font-black text-white">{totalClaimableMissions}</span>
                  </div>
                )}
              </button>
              <span className="text-[9px] font-black text-white/80 uppercase tracking-wider drop-shadow-sm select-none">{t('home.nav.missions')}</span>
            </div>
            
            {/* Daily */}
            <div className="flex flex-col items-center gap-1">
              <button 
                onClick={onShowDailyDrop}
                aria-label="Open daily rewards calendar"
                className="relative w-11 h-11 rounded-full flex items-center justify-center bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-[#d8b4fe] via-[#8b5cf6] to-[#6d28d9] text-white shadow-xl active:scale-90 transition-all toy-3d-button border-b-4 border-[#4c1d95]/60"
              >
                <FeatureIcon type="calendar" size="sm" className="brightness-110 saturate-125" />
                {!user?.dailyGift?.isClaimedToday && (
                  <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#f43f5e] border-2 border-white rounded-full shadow-lg animate-pulse z-20" />
                )}
              </button>
              <span className="text-[9px] font-black text-white/80 uppercase tracking-wider drop-shadow-sm select-none">{t('home.nav.daily')}</span>
            </div>
 
            {/* News */}
            <div className="flex flex-col items-center gap-1">
              <button 
                onClick={() => {
                  audioEngine.playSound('click');
                  setShowNewsModal(true);
                  setHasNewNews(false);
                  if (news && news.length > 0) {
                    localStorage.setItem('nr_news_read_version', news[0].id);
                  } else {
                    localStorage.setItem('nr_news_read_version', 'v1');
                  }
                }}
                aria-label="Open game news"
                className="relative w-11 h-11 rounded-full flex items-center justify-center bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-[#7dd3fc] via-[#0284c7] to-[#0369a1] text-white shadow-xl active:scale-90 transition-all toy-3d-button border-b-4 border-[#0c4a6e]/60"
              >
                <FeatureIcon type="news" size="sm" className="brightness-110 saturate-125" />
                {hasNewNews && (
                  <div className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#f43f5e] border-2 border-white rounded-full shadow-lg animate-pulse z-20" />
                )}
              </button>
              <span className="text-[9px] font-black text-white/80 uppercase tracking-wider drop-shadow-sm select-none">{t('home.nav.news')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Milestone Offer Badge (Centered wrapper to keep it inside the inner screen bounds) */}
      {user?.activeMilestoneOffer && offerTimeLeft && (
        <div className="absolute top-[180px] left-0 right-0 z-[90] flex justify-center pointer-events-none">
          <div className="w-full max-w-md relative pointer-events-auto">
            <div className="absolute right-8 flex flex-col items-center gap-1.5">
              <button
                onClick={() => {
                  audioEngine.playSound('click');
                  setShowMilestonePopup(true);
                }}
                aria-label="Open active milestone offer"
                className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 border-2 border-amber-300 shadow-xl flex items-center justify-center active:scale-95 transition-all animate-[bounce_3s_infinite] play-button-glow cursor-pointer relative"
              >
                <FeatureIcon type="milestone" size="md" className="w-8 h-8" />
                {/* Glowing outer ring */}
                <div className="absolute inset-0 rounded-full border-2 border-amber-300 animate-ping opacity-25" />
              </button>
              <div className="bg-slate-900/50 backdrop-blur-md px-2.5 py-0.5 rounded-full border border-amber-400/50 shadow-[0_0_12px_rgba(251,191,36,0.8)] animate-pulse mt-1 z-10">
                <span className="font-num text-[8px] font-black text-white/90 tracking-wider tabular-nums">{offerTimeLeft}</span>
              </div>
            </div>
          </div>
        </div>
      )}
      </div> {/* End flex-1 */}

        <AnimatePresence>
          {showNicknameModal && (
            <div className="absolute inset-0 z-[1001] flex items-center justify-center p-6">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
              />
              <motion.div 
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                className="game-card-3d border-none w-full max-w-sm p-8 relative z-10"
              >
                <h3 className="text-3xl font-num text-center mb-4 tracking-tight text-slate-900">{t('home.nickname.title')}</h3>
                <p className="text-slate-500 text-center mb-8 font-black uppercase tracking-widest text-[10px]">{t('home.nickname.subtitle')}</p>
                
                <input 
                  type="text" 
                  value={nicknameInput}
                  onChange={(e) => {
                    setNicknameInput(e.target.value);
                    setNicknameError(null);
                  }}
                  placeholder={t('home.nickname.placeholder')}
                  className={cn(
                    "w-full bg-slate-50 p-5 rounded-2xl border-none shadow-inner mb-2 font-black text-center focus:outline-none transition-colors text-slate-900 placeholder:text-slate-300 clay-base",
                    nicknameError ? "border-rose-500" : "focus:border-[#0ea5e9]"
                  )}
                />

                {nicknameError && (
                  <p className="text-rose-500 text-[10px] font-black uppercase tracking-widest text-center mb-4">
                    {nicknameError}
                  </p>
                )}

                <div className="mb-6" />

                <button
                  onClick={handleUpdateNickname}
                  disabled={updateStatus !== 'idle'}
                  className={cn(
                    "w-full font-num text-2xl py-5 border-none toy-3d-button",
                    updateStatus === 'idle' && "bg-[#fbbf24] text-slate-900",
                    updateStatus === 'checking' && "bg-sky-400 text-white cursor-wait",
                    updateStatus === 'saving' && "bg-yellow-400 text-slate-900 translate-y-2 clay-base-active",
                    updateStatus === 'success' && "bg-green-500 text-white scale-105",
                    updateStatus === 'error' && "bg-rose-500 text-white"
                  )}
                >
                  <motion.div
                    animate={updateStatus === 'error' ? { x: [-4, 4, -4, 4, 0] } : {}}
                    transition={{ duration: 0.4 }}
                    className="flex items-center justify-center gap-2"
                  >
                    {updateStatus === 'checking' && <Loader2 className="w-6 h-6 animate-spin" />}
                    {updateStatus === 'success' && <CheckCircle2 className="w-6 h-6" />}
                    {updateStatus === 'error' && <AlertCircle className="w-6 h-6" />}
                    
                    <span>
                      {updateStatus === 'idle' && t('home.nickname.confirm')}
                      {updateStatus === 'checking' && t('home.nickname.checking')}
                      {updateStatus === 'saving' && t('home.nickname.saving')}
                      {updateStatus === 'success' && t('home.nickname.success')}
                      {updateStatus === 'error' && t('home.nickname.rejected')}
                    </span>
                  </motion.div>
                </button>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

      <AnimatePresence>
        {showPrismUnboxing && latestUnboxedPrism && (
          <PrismUnboxingModal 
            prism={latestUnboxedPrism}
            onClose={() => setShowPrismUnboxing(false)}
          />
        )}
      </AnimatePresence>


      <AnimatePresence>
        {showPremiumModal && (
          <PremiumModal 
            user={user}
            onClose={() => setShowPremiumModal(false)}
            onSetPremiumStatus={onSetPremiumStatus}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showMilestonePopup && user?.activeMilestoneOffer && (
          (() => {
            const activeOffer = MILESTONE_OFFERS.find(o => o.id === user.activeMilestoneOffer?.id);
            if (!activeOffer) return null;

            return (
              <div className="absolute inset-0 z-[10005] flex items-center justify-center p-6">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowMilestonePopup(false)}
                  className="absolute inset-0 bg-slate-955/80 backdrop-blur-md"
                />
                
                <motion.div
                  initial={{ scale: 0.9, opacity: 0, y: 20 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  exit={{ scale: 0.9, opacity: 0, y: 20 }}
                  className="relative overflow-hidden bg-gradient-to-br from-amber-600 to-amber-955 border-2 border-amber-400 rounded-[32px] p-6 shadow-2xl text-white w-full max-w-sm z-10 flex flex-col gap-4"
                >
                  <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.05)_50%,transparent_75%)] bg-[length:250px_250px] animate-[pulse_3s_infinite] pointer-events-none" />
                  
                  <button 
                    onClick={() => {
                      audioEngine.playSound('click');
                      setShowMilestonePopup(false);
                    }}
                    className="absolute top-4 right-4 p-2 bg-black/40 hover:bg-black/60 rounded-full transition-colors border border-white/10"
                  >
                    <X className="w-4 h-4 text-white/80" />
                  </button>

                  <div className="flex flex-col items-center text-center mt-2">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 border-2 border-amber-200 shadow-lg flex items-center justify-center mb-3 animate-[bounce_3s_infinite] play-button-glow">
                      <Gift className="w-8 h-8 text-amber-950" />
                    </div>
                    
                    <span className="text-[10px] font-black tracking-widest text-amber-400 uppercase leading-none mb-1">
                      {t('home.milestone.limited_offer')}
                    </span>
                    <h3 className="text-2xl font-num font-black uppercase text-amber-300 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                      {activeOffer.name}
                    </h3>
                    
                    {offerTimeLeft && (
                      <div className="mt-2 bg-black/40 backdrop-blur-md px-3 py-1 rounded-full border border-white/10 flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-amber-400 animate-pulse" />
                        <span className="font-num font-black text-xs text-amber-300 tabular-nums">{offerTimeLeft}</span>
                      </div>
                    )}
                  </div>

                  <p className="text-[11px] text-white/90 font-black tracking-wide leading-relaxed text-center bg-black/20 p-3 rounded-2xl border border-white/5">
                    {activeOffer.description}
                  </p>

                  <div className="bg-black/35 backdrop-blur-md border border-white/10 rounded-2xl p-4 flex flex-col gap-2 text-left">
                    <span className="text-[9px] font-black tracking-widest text-amber-400 uppercase">{t('home.milestone.unlocked_items')}</span>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <div className="flex items-center gap-1.5 bg-blue-500/25 border border-blue-400/35 px-2.5 py-1 rounded-xl shadow-sm">
                        <CurrencyIcon type="prism" size="sm" stars={3} color="blue" />
                        <span className="font-num text-xs font-black text-blue-300">{activeOffer.rewards.prisms.count}x</span>
                      </div>
                      <div className="flex items-center gap-1.5 bg-cyan-500/25 border border-cyan-400/35 px-2.5 py-1 rounded-xl shadow-sm">
                        <ChestIcon tier="diamond" size="xs" className="!w-4 !h-[14px]" />
                        <span className="font-num text-xs font-black text-cyan-300">{activeOffer.rewards.boxes.diamond}x</span>
                      </div>
                      {activeOffer.rewards.starTickets > 0 && (
                        <div className="flex items-center gap-1.5 bg-yellow-500/25 border border-yellow-400/35 px-2.5 py-1 rounded-xl shadow-sm">
                          <CurrencyIcon type="ticket_star" size="sm" />
                          <span className="font-num text-xs font-black text-yellow-300">{activeOffer.rewards.starTickets}x</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 bg-yellow-400/25 border border-yellow-400/35 px-2.5 py-1 rounded-xl shadow-sm">
                        <CurrencyIcon type="coin" size="sm" />
                        <span className="font-num text-xs font-black text-yellow-200">{activeOffer.rewards.coins.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-1.5 bg-purple-500/25 border border-purple-400/35 px-2.5 py-1 rounded-xl shadow-sm">
                        <CurrencyIcon type="gem" size="sm" />
                        <span className="font-num text-xs font-black text-purple-300">{activeOffer.rewards.gems.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 mt-2">
                    <button
                      onClick={async () => {
                        audioEngine.playSound('click');
                        if (!purchaseManager.isAndroid()) {
                          setSimulatingMilestoneOffer(activeOffer);
                          return;
                        }
                        
                        setIsMilestoneProcessing(activeOffer.id);
                        try {
                          await purchaseManager.buyMilestoneOffer(activeOffer.id);
                        } catch (error) {
                          console.error(error);
                          onShowAlert("ERROR", t('home.milestone.purchase_error'), "error");
                        } finally {
                          setIsMilestoneProcessing(null);
                        }
                      }}
                      disabled={isMilestoneProcessing === activeOffer.id}
                      className="w-full bg-amber-400 hover:bg-amber-300 text-amber-950 font-num font-black text-xl py-4 rounded-2xl border-none shadow-lg transition-all active:scale-95 duration-150 uppercase flex items-center justify-center cursor-pointer"
                    >
                      {isMilestoneProcessing === activeOffer.id ? (
                        <Loader2 className="w-6 h-6 animate-spin" />
                      ) : (
                        t('home.milestone.buy_now', { price: activeOffer.price })
                      )}
                    </button>
                    <button
                      onClick={() => {
                        audioEngine.playSound('click');
                        setShowMilestonePopup(false);
                      }}
                      className="w-full py-2 bg-transparent text-white/50 hover:text-white font-num text-xs uppercase tracking-widest transition-colors cursor-pointer"
                    >
                      {t('home.milestone.no_thanks')}
                    </button>
                  </div>
                </motion.div>
              </div>
            );
          })()
        )}
      </AnimatePresence>

      <AnimatePresence>
        {simulatingMilestoneOffer && (
          <div className="absolute inset-0 z-[10006] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" 
              onClick={() => setSimulatingMilestoneOffer(null)} 
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }} 
              animate={{ scale: 1, opacity: 1, y: 0 }} 
              exit={{ scale: 0.9, opacity: 0, y: 20 }} 
              className="bg-slate-900 border-2 border-amber-400/50 rounded-[40px] w-full max-w-sm overflow-hidden relative shadow-2xl z-10 p-8 text-white text-center"
            >
              <Gift className="w-16 h-16 text-amber-400 mx-auto mb-4 animate-[bounce_2s_infinite]" />
              
              <h3 className="text-2xl font-num text-amber-300 uppercase tracking-tight leading-none mb-2">{t('home.milestone.iap_simulator_title')}</h3>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">{t('home.milestone.iap_simulator_sub')}</p>
              
              <div className="bg-slate-950/50 border border-white/5 rounded-2xl p-5 mb-8 text-left space-y-3">
                <div className="flex justify-between items-center border-b border-white/5 pb-2">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{t('home.milestone.product_id')}</span>
                  <span className="font-num text-xs font-bold text-amber-300">{simulatingMilestoneOffer.id}</span>
                </div>
                <div className="flex justify-between items-center border-b border-white/5 pb-2">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{t('home.milestone.bundle_title')}</span>
                  <span className="text-xs font-bold uppercase text-white">{simulatingMilestoneOffer.name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{t('home.milestone.price_mock')}</span>
                  <span className="font-num text-sm font-black text-emerald-400">{simulatingMilestoneOffer.price}</span>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <button 
                  onClick={async () => {
                    audioEngine.playSound('click');
                    if (onGrantMilestoneOfferRewards) {
                      setIsMilestoneProcessing(simulatingMilestoneOffer.id);
                      const success = await onGrantMilestoneOfferRewards(simulatingMilestoneOffer.id);
                      if (success) {
                        setSimulatingMilestoneOffer(null);
                        setShowMilestonePopup(false);
                        audioEngine.playClaim();
                        onShowAlert(t('home.milestone.purchase_success_title'), t('home.milestone.purchase_success_msg'), "success");
                      } else {
                        onShowAlert("ERROR", t('home.milestone.reward_error'), "error");
                      }
                      setIsMilestoneProcessing(null);
                    }
                  }}
                  disabled={isMilestoneProcessing === simulatingMilestoneOffer.id}
                  className="w-full py-4 bg-emerald-500 text-white font-num text-lg uppercase tracking-wider rounded-2xl hover:bg-emerald-400 transition-colors shadow-lg active:scale-95 disabled:opacity-50"
                >
                  {isMilestoneProcessing === simulatingMilestoneOffer.id ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : t('home.milestone.complete_transaction')}
                </button>
                
                <button 
                  onClick={() => {
                    audioEngine.playSound('click');
                    setSimulatingMilestoneOffer(null);
                  }}
                  disabled={isMilestoneProcessing === simulatingMilestoneOffer.id}
                  className="w-full py-4 bg-slate-800 text-slate-400 font-num text-lg uppercase tracking-wider rounded-2xl hover:bg-slate-700 transition-colors active:scale-95 disabled:opacity-50"
                >
                  {t('home.milestone.cancel')}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showMainMenu && (
          <div className="absolute inset-0 z-[9999] flex flex-col">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMainMenu(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-md"
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="absolute inset-0 bg-slate-100 flex flex-col overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center gap-4 p-6 pb-4 shrink-0 bg-white shadow-sm z-10">
                <button
                  onClick={() => {
                    audioEngine.playSound('click');
                    setShowMainMenu(false);
                  }}
                  className="p-3 bg-white rounded-2xl toy-3d-button border-none shadow-sm"
                >
                  <ArrowLeft className="w-6 h-6 text-slate-400" />
                </button>
                <h3 className="text-3xl font-num font-black tracking-tight uppercase text-slate-900">{t('home.menu.title')}</h3>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* User Info (Profile Summary Box) */}
                <button
                  onClick={() => {
                    audioEngine.playSound('click');
                    onShowProfile();
                  }}
                  className="w-full bg-white p-6 rounded-[32px] border-none flex flex-col gap-5 text-left shadow-md active:translate-y-1 transition-all relative overflow-hidden border-b-8 border-slate-200 group"
                >
                  <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'linear-gradient(90deg, #000 1px, transparent 1px)', backgroundSize: '4px 100%' }} />
                  <div className="absolute inset-0 bg-slate-50/50" style={{ clipPath: 'polygon(0 0, 100% 0, 50% 50%)' }} />

                  <div className="flex items-center gap-4 relative z-10">
                    <GalleryPhoto id={user.avatarId || 'g1'} className="w-20 h-20 shrink-0 drop-shadow-md rounded-2xl" />
                    <div className="flex-1">
                      <div className="flex flex-col gap-1 min-w-0">
                        <div
                          className="font-num leading-tight text-slate-900 uppercase tracking-tighter whitespace-normal break-words"
                          style={{
                            fontSize: (user.nickname || user.displayName || t('home.player_fallback')).length > 12 ? '1rem' :
                                      (user.nickname || user.displayName || t('home.player_fallback')).length > 8 ? '1.25rem' : '1.75rem',
                          }}
                        >
                          {user.nickname || user.displayName || t('home.player_fallback')}
                        </div>
                        <div className="flex items-center gap-1 bg-[#fbbf24] text-slate-900 text-[10px] font-black px-2 py-0.5 rounded-lg shadow-sm border-b-2 border-amber-600 self-start">
                          {user.isPremium && <Crown className="w-3 h-3 text-slate-900 fill-slate-900 shrink-0" />}
                          <span>LVL {user.level}</span>
                        </div>
                      </div>
                      <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mt-1 opacity-60">{t('home.menu.view_profile')}</div>
                    </div>
                  </div>

                  <div className="w-full space-y-1.5 relative z-10">
                    <div className="flex justify-between text-[8px] font-black text-slate-500 uppercase tracking-widest opacity-60">
                      <span>{t('home.menu.exp_progress')}</span>
                      <span className="text-slate-900">{user.exp} / {user.level * 1000}</span>
                    </div>
                    <div className="h-5 bg-slate-100 rounded-full overflow-hidden shadow-inner p-1 relative border-none">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(user.exp / (user.level * 1000)) * 100}%` }}
                        className="h-full bg-gradient-to-br from-[#fbbf24] to-[#f43f5e] rounded-full relative overflow-hidden"
                      >
                         <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '4px 100%' }} />
                      </motion.div>
                    </div>
                  </div>
                </button>

                {/* Premium Upsell - Full Width */}
                <button
                  onClick={() => {
                    audioEngine.playSound('click');
                    setShowPremiumModal(true);
                  }}
                  className={cn(
                    "w-full p-4 rounded-3xl border-none shadow-lg flex items-center justify-between group active:scale-95 transition-all overflow-hidden relative cursor-pointer",
                    user.isPremium
                      ? "bg-gradient-to-r from-purple-500 to-purple-600"
                      : "bg-gradient-to-r from-amber-400 to-amber-500"
                  )}
                >
                  <div className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-in-out" />
                  <div className="flex items-center gap-3 relative z-10">
                    <div className="p-2 bg-white/20 rounded-xl">
                      <Crown className="w-5 h-5 text-white fill-white" />
                    </div>
                    <div className="text-left">
                      <div className="text-white font-black text-sm uppercase tracking-tight">
                        {user.isPremium ? t('home.menu.pro_member') : t('home.menu.upgrade_to_pro')}
                      </div>
                      <div className={cn(
                        "text-[8px] font-black uppercase tracking-widest leading-none mt-0.5",
                        user.isPremium ? "text-purple-200" : "text-amber-900/50"
                      )}>
                        {t('home.menu.pro_benefits')}
                      </div>
                    </div>
                  </div>
                  <Star className="w-5 h-5 text-white relative z-10" />
                </button>

                {/* 2-Column Grid for the Rest */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Inventory Box */}
                  <button
                    onClick={() => {
                      audioEngine.playSound('click');
                      onShowInventory();
                    }}
                    className="bg-white p-5 flex flex-col items-center gap-3 text-center toy-3d-button border-none relative overflow-hidden"
                  >
                    <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: 'linear-gradient(45deg, #000 1px, transparent 1px)', backgroundSize: '4px 4px' }} />
                    <div className="w-14 h-14 shrink-0 bg-emerald-100 rounded-2xl border-b-4 border-emerald-200 relative overflow-hidden flex items-center justify-center">
                       <ChestIcon tier="silver" size="sm" className="relative z-10" />
                    </div>
                    <div className="relative z-10">
                      <div className="font-black text-slate-900 text-base uppercase tracking-tight">{t('home.menu.inventory')}</div>
                      <div className="text-[9px] text-slate-500 font-black uppercase tracking-widest opacity-60">{t('home.menu.inventory_sub')}</div>
                    </div>
                    {user && (user.rewardBoxes.bronze > 0 || user.rewardBoxes.silver > 0 || user.rewardBoxes.gold > 0 || user.rewardBoxes.diamond > 0) && (
                      <div className="absolute top-4 right-4 w-3 h-3 bg-rose-500 border-2 border-white rounded-full shadow-sm animate-pulse" />
                    )}
                  </button>

                  {/* Missions Box */}
                  <button
                    onClick={() => {
                      audioEngine.playSound('click');
                      onShowMissions();
                    }}
                    className="bg-white p-5 flex flex-col items-center gap-3 text-center toy-3d-button border-none relative overflow-hidden"
                  >
                    <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: 'linear-gradient(45deg, #000 1px, transparent 1px)', backgroundSize: '4px 4px' }} />
                    <div className="w-14 h-14 shrink-0 bg-teal-100 rounded-2xl border-b-4 border-teal-200 relative overflow-hidden flex items-center justify-center">
                       <FeatureIcon type="checklist" size="sm" className="relative z-10 brightness-75 scale-125" />
                    </div>
                    <div className="relative z-10">
                      <div className="font-black text-slate-900 text-base uppercase tracking-tight">{t('home.menu.missions')}</div>
                      <div className="text-[9px] text-slate-500 font-black uppercase tracking-widest opacity-60">{t('home.menu.missions_sub')}</div>
                    </div>
                    {totalClaimableMissions > 0 && (
                      <div className="absolute top-3 right-3 w-6 h-6 bg-rose-500 border-2 border-white rounded-full shadow-sm flex items-center justify-center animate-bounce">
                         <span className="text-[10px] font-black text-white">{totalClaimableMissions}</span>
                      </div>
                    )}
                  </button>

                  {/* News Box */}
                  <button
                    onClick={() => {
                      audioEngine.playSound('click');
                      setShowNewsModal(true);
                    }}
                    className="bg-white p-5 flex flex-col items-center gap-3 text-center toy-3d-button border-none relative overflow-hidden"
                  >
                    <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: 'linear-gradient(45deg, #000 1px, transparent 1px)', backgroundSize: '4px 4px' }} />
                    <div className="w-14 h-14 shrink-0 bg-orange-100 rounded-2xl border-b-4 border-orange-200 relative overflow-hidden flex items-center justify-center">
                       <MessageSquareText className="w-6 h-6 text-orange-500 relative z-10" />
                    </div>
                    <div className="relative z-10">
                      <div className="font-black text-slate-900 text-base uppercase tracking-tight">{t('home.menu.news')}</div>
                      <div className="text-[9px] text-slate-500 font-black uppercase tracking-widest opacity-60">{t('home.menu.news_sub')}</div>
                    </div>
                    {news.some(n => !n.read) && (
                      <div className="absolute top-4 right-4 w-3 h-3 bg-[#f43f5e] rounded-full shadow-md animate-pulse border-2 border-white" />
                    )}
                  </button>

                  {/* Altar Box */}
                  <button
                    onClick={() => {
                      audioEngine.playSound('click');
                      onShowPrismManager();
                    }}
                    className="bg-white p-5 flex flex-col items-center gap-3 text-center toy-3d-button border-none relative overflow-hidden"
                  >
                    <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: 'linear-gradient(45deg, #000 1px, transparent 1px)', backgroundSize: '4px 4px' }} />
                    <div className="w-14 h-14 shrink-0 bg-cyan-100 rounded-2xl border-b-4 border-cyan-200 relative overflow-hidden flex items-center justify-center">
                       <Sparkles className="w-6 h-6 text-cyan-500 relative z-10" />
                    </div>
                    <div className="relative z-10">
                      <div className="font-black text-slate-900 text-base uppercase tracking-tight">{t('home.menu.altar')}</div>
                      <div className="text-[9px] text-slate-500 font-black uppercase tracking-widest opacity-60">{t('home.menu.altar_sub')}</div>
                    </div>
                  </button>

                  {/* Settings Box */}
                  <button
                    onClick={() => {
                      audioEngine.playSound('click');
                      onShowSettings();
                    }}
                    className="col-span-2 bg-white p-5 flex flex-row items-center justify-center gap-4 text-center toy-3d-button border-none relative overflow-hidden"
                  >
                    <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{ backgroundImage: 'linear-gradient(45deg, #000 1px, transparent 1px)', backgroundSize: '4px 4px' }} />
                    <div className="w-10 h-10 shrink-0 bg-slate-100 rounded-2xl border-b-4 border-slate-200 relative overflow-hidden flex items-center justify-center">
                       <SettingsIcon className="w-5 h-5 text-slate-400 relative z-10" />
                    </div>
                    <div className="relative z-10 text-left">
                      <div className="font-black text-slate-900 text-base uppercase tracking-tight">{t('home.menu.settings')}</div>
                      <div className="text-[9px] text-slate-500 font-black uppercase tracking-widest opacity-60">{t('home.menu.settings_sub')}</div>
                    </div>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showNewsModal && (
          <div className="absolute inset-0 z-[10000] flex flex-col">
            {/* Full-screen backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-900/70 backdrop-blur-md"
            />

            {/* Full-screen slide-up sheet */}
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="absolute inset-0 bg-gradient-to-b from-slate-50 to-white flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center gap-4 px-6 pt-8 pb-4 border-b border-slate-200/60 shrink-0">
                <button
                  onClick={() => {
                    audioEngine.playSound('click');
                    setShowNewsModal(false);
                  }}
                  className="p-3 bg-white rounded-2xl toy-3d-button border-none shadow-sm"
                >
                  <ArrowLeft className="w-6 h-6 text-slate-400" />
                </button>
                <div>
                  <h3 className="text-2xl font-num font-black tracking-tight uppercase text-slate-900 leading-none">{t('home.news.title')}</h3>
                  <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest mt-1">{t('home.news.subtitle')}</p>
                </div>
              </div>

              {/* News list */}
              <div className="flex-1 overflow-y-auto scrollbar-none px-5 py-5 space-y-3">
                {(() => {
                  const iconMap: Record<string, React.ComponentType<any>> = {
                    Zap,
                    Sparkles,
                    Heart,
                    Star,
                    Trophy,
                    Target,
                    Apple: AppleIcon,
                    Ticket,
                    ShoppingBag,
                    MessageSquareText,
                    FileText,
                    Timer,
                    Clock,
                    ListChecks,
                    Coins,
                    AlertCircle,
                    Gift,
                  };

                  const defaultNews = [
                    {
                      id: 'news-1',
                      category: t('common.update'),
                      icon: 'Zap',
                      iconColor: 'text-cyan-500 fill-cyan-500',
                      title: t('home.news.default_1_title'),
                      detail: t('home.news.default_1_detail')
                    },
                    {
                      id: 'news-2',
                      category: t('common.reward'),
                      icon: 'Sparkles',
                      iconColor: 'text-amber-500 fill-amber-400',
                      title: t('home.news.default_2_title'),
                      detail: t('home.news.default_2_detail')
                    },
                    {
                      id: 'news-3',
                      category: t('common.event'),
                      icon: 'Heart',
                      iconColor: 'text-rose-500 fill-rose-500 animate-pulse',
                      title: t('home.news.default_3_title'),
                      detail: t('home.news.default_3_detail')
                    }
                  ];

                  const activeNews = (news && news.length > 0) ? news : defaultNews;

                  const getCategoryConfig = (category: string) => {
                    const cat = (category || '').toLowerCase();
                    if (cat.includes('update')) return { icon: Zap, iconColor: 'text-cyan-500 fill-cyan-500' };
                    if (cat.includes('event')) return { icon: Sparkles, iconColor: 'text-amber-500 fill-amber-400' };
                    if (cat.includes('reward')) return { icon: Gift, iconColor: 'text-rose-500 fill-rose-500 animate-pulse' };
                    if (cat.includes('notice')) return { icon: AlertCircle, iconColor: 'text-red-500' };
                    if (cat.includes('tip')) return { icon: FileText, iconColor: 'text-emerald-500' };
                    if (cat.includes('ranking')) return { icon: Trophy, iconColor: 'text-yellow-500' };
                    return { icon: MessageSquareText, iconColor: 'text-orange-500' };
                  };

                  return activeNews.map((item, idx) => {
                    let IconComponent = MessageSquareText;
                    let iconColor = 'text-orange-500';

                    if (item.icon) {
                      IconComponent = typeof item.icon === 'string'
                        ? (iconMap[item.icon] || MessageSquareText)
                        : (item.icon || MessageSquareText);
                      iconColor = item.iconColor || 'text-orange-500';
                    } else {
                      const config = getCategoryConfig(item.category);
                      IconComponent = config.icon;
                      iconColor = config.iconColor;
                    }

                    return (
                      <motion.button
                        key={item.id}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.06, duration: 0.25 }}
                        onClick={() => {
                          audioEngine.playSound('click');
                          setSelectedNewsItem(item);
                        }}
                        className="w-full bg-white p-4 rounded-3xl border-b-4 border-slate-200 shadow-sm text-left transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0.5 focus:outline-none cursor-pointer flex items-center gap-4"
                      >
                        {/* Icon badge */}
                        <div className="w-12 h-12 shrink-0 rounded-2xl bg-slate-100 flex items-center justify-center border-b-2 border-slate-200">
                          <IconComponent className={cn('w-6 h-6', iconColor)} />
                        </div>
                        {/* Text */}
                        <div className="flex-1 min-w-0">
                          <div className="text-[9px] font-black uppercase tracking-wider text-slate-400 mb-0.5">{item.category}</div>
                          <h4 className="font-num text-base font-black text-slate-800 uppercase tracking-tight truncate">{item.title}</h4>
                        </div>
                        {/* Chevron */}
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-4 h-4 text-slate-400 shrink-0" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 6 15 12 9 18" />
                        </svg>
                      </motion.button>
                    );
                  });
                })()}
              </div>

              {/* Footer button */}
              <div className="px-5 pb-8 pt-3 shrink-0">
                <button
                  onClick={() => {
                    audioEngine.playSound('click');
                    setShowNewsModal(false);
                  }}
                  className="w-full bg-orange-500 text-white font-num text-lg py-4 rounded-2xl shadow-md active:translate-y-1 transition-all uppercase tracking-widest toy-3d-button border-none"
                >
                  {t('home.news.awesome')}
                </button>
              </div>
            </motion.div>

            {/* Detail sub-modal */}
            <AnimatePresence>
              {selectedNewsItem && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setSelectedNewsItem(null)}
                    className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm z-10"
                  />
                  <motion.div
                    initial={{ scale: 0.88, opacity: 0, y: 40 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.88, opacity: 0, y: 40 }}
                    transition={{ type: 'spring', damping: 26, stiffness: 280 }}
                    className="absolute inset-x-4 top-1/2 -translate-y-1/2 z-20 bg-white rounded-3xl shadow-2xl p-6 flex flex-col gap-4"
                  >
                    {/* Detail header */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        {(() => {
                          const iconMap2: Record<string, React.ComponentType<any>> = { Zap, Sparkles, Heart, Star, Trophy, Target, Apple: AppleIcon, Ticket, ShoppingBag, MessageSquareText, FileText, Timer, Clock, ListChecks, Coins, AlertCircle, Gift };
                          let IconComponent2 = MessageSquareText;
                          let iconColor2 = 'text-orange-500';
                          if (selectedNewsItem.icon) {
                            IconComponent2 = typeof selectedNewsItem.icon === 'string' ? (iconMap2[selectedNewsItem.icon] || MessageSquareText) : (selectedNewsItem.icon as any || MessageSquareText);
                            iconColor2 = selectedNewsItem.iconColor || 'text-orange-500';
                          }
                          return (
                            <div className="flex items-center gap-2 mb-2">
                              <IconComponent2 className={cn('w-5 h-5', iconColor2)} />
                              <span className="text-[9px] font-black uppercase tracking-wider text-slate-400">{selectedNewsItem.category}</span>
                            </div>
                          );
                        })()}
                        <h3 className="font-num text-xl font-black text-slate-900 uppercase tracking-tight leading-tight">{selectedNewsItem.title}</h3>
                      </div>
                      <button
                        onClick={() => setSelectedNewsItem(null)}
                        className="p-2 rounded-xl bg-slate-100 border-2 border-slate-200 hover:bg-slate-200 transition-all shrink-0"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-4 h-4 text-slate-500" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                    {/* Divider */}
                    <div className="h-px bg-slate-100" />
                    {/* Description */}
                    <p className="text-sm text-slate-600 leading-relaxed">{selectedNewsItem.detail}</p>
                    {/* Close */}
                    <button
                      onClick={() => {
                        audioEngine.playSound('click');
                        setSelectedNewsItem(null);
                      }}
                      className="w-full bg-slate-900 text-white font-num text-base py-3.5 rounded-2xl shadow-md active:translate-y-0.5 transition-all uppercase tracking-widest border-none mt-1"
                    >
                      {t('home.news.got_it')}
                    </button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        )}
      </AnimatePresence>

      {/* bottom bar moved outside flex-1 for z-index safety */}
      {!hideBottomNav && !showNicknameModal && !(user?.nickname === null && (user?.isTutorialCompleted || user?.tutorialStep === 36)) && (
        <div className="absolute bottom-14 left-0 right-0 z-[5000] flex justify-center items-end gap-3 px-4 pb-[env(safe-area-inset-bottom)] mb-2">
          {/* Menu - Most Left */}
          <button
            id="home-menu-button"
            onClick={() => {
              console.log("Menu button clicked!");
              audioEngine.playSound('click');
              setShowMainMenu(true);
            }}
            className="relative w-16 h-16 rounded-[24px] flex flex-col items-center justify-center bg-sky-500 shadow-2xl active:scale-95 transition-all text-white toy-3d-button border-b-[6px] border-sky-700/50 group gap-1"
          >
            <FeatureIcon type="menu" size="md" className="brightness-125 saturate-150" />
            <span className="text-[8px] font-black uppercase tracking-tight text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] group-active:translate-y-0.5 transition-transform">{t('home.nav.menu')}</span>
            {user && ((user.rewardBoxes.bronze > 0 || user.rewardBoxes.silver > 0 || user.rewardBoxes.gold > 0 || user.rewardBoxes.diamond > 0) || totalClaimableMissions > 0) && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#f43f5e] border-2 border-white rounded-full shadow-lg animate-pulse z-20" />
            )}
          </button>
 
          {/* Ranking */}
          <button
            onClick={onShowLeaderboard}
            className="w-16 h-16 rounded-[24px] flex flex-col items-center justify-center bg-amber-500 shadow-2xl active:scale-95 transition-all text-white toy-3d-button border-b-[6px] border-amber-700/50 group gap-1"
          >
            <FeatureIcon type="trophy" size="md" className="brightness-110 saturate-150" />
            <span className="text-[8px] font-black uppercase tracking-tight text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] group-active:translate-y-0.5 transition-transform">{t('home.nav.ranking')}</span>
          </button>
 
          {/* Play Button - Large, Central, Glowing */}
          <div className="relative mb-1">
            <button
              id="home-play-button"
              onClick={() => onShowChooseMode()}
              onMouseEnter={() => setIsHoveringPlay(true)}
              onMouseLeave={() => setIsHoveringPlay(false)}
              className="w-24 h-24 rounded-[36px] flex flex-col items-center justify-center gap-1 bg-yellow-400 transition-all active:scale-90 shadow-[0_0_25px_rgba(250,204,21,0.5)] play-button-glow text-slate-900 relative toy-3d-button border-b-[8px] border-yellow-600/50"
            >
              <FeatureIcon type="play" size="lg" className="brightness-110" />
              <span className="text-[10px] font-black uppercase tracking-widest leading-none">{t('home.nav.play')}</span>
            </button>
          </div>
 
          {/* Altar Shortcut */}
          <button
            id="home-anima-button"
            onClick={() => onShowPrismManager()}
            className="w-16 h-16 rounded-[24px] flex flex-col items-center justify-center bg-cyan-500 shadow-2xl active:scale-95 transition-all text-white toy-3d-button border-b-[6px] border-cyan-700/50 group gap-1"
          >
            <FeatureIcon type="altar" size="md" className="brightness-125 saturate-150" />
            <span className="text-[8px] font-black uppercase tracking-tight text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] group-active:translate-y-0.5 transition-transform">{t('home.nav.anima')}</span>
          </button>
 
          {/* Shop - Most Right */}
          <button
            id="home-shop-button"
            onClick={() => onShowShop()}
            className="w-16 h-16 rounded-[24px] flex flex-col items-center justify-center bg-rose-500 shadow-2xl active:scale-95 transition-all text-white toy-3d-button border-b-[6px] border-rose-700/50 group gap-1"
          >
            <FeatureIcon type="shop" size="md" className="brightness-125 saturate-150" />
            <span className="text-[8px] font-black uppercase tracking-tight text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] group-active:translate-y-0.5 transition-transform">{t('home.nav.shop')}</span>
          </button>
        </div>
      )}
    </div>
  );
}
